<template>
  <v-dialog
    :value="isOpen"
    max-width="520"
    scrollable
    @input="val => !val && $emit('close')"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>集計表へ登録</span>
        <v-btn
          icon
          @click="$emit('close')"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="mb-4">
          <span class="font-weight-bold">{{ userName }}</span>
          さんを、選択した集計テンプレートに含まれる集計表へ登録します。
        </div>

        <v-skeleton-loader
          v-if="loading"
          type="list-item-two-line"
        />
        <template v-else>
          <v-alert
            v-if="templates.length === 0"
            type="info"
            text
          >
            集計テンプレートが登録されていません。
          </v-alert>
          <template v-else>
            <v-select
              v-model="selectedTemplate"
              :items="templates"
              item-text="attributes.name"
              item-value="id"
              return-object
              label="集計テンプレート"
              no-data-text="利用可能なデータがありません"
              class="mb-4"
            />

            <!-- 選択したテンプレートに含まれる集計表のプレビュー -->
            <div v-if="selectedTemplate">
              <v-divider class="mb-4" />
              <v-card
                v-for="group in selectedTemplate.attributes.aggregations"
                :key="group.type"
                outlined
                class="pa-3 mb-3"
              >
                <div class="font-weight-medium mb-2">
                  {{ group.type }}
                </div>
                <v-chip-group v-if="group.aggregations.length > 0">
                  <v-chip
                    v-for="aggregation in group.aggregations"
                    :key="aggregation.id"
                    small
                    outlined
                    disabled
                    class="ma-1"
                  >
                    {{ aggregation.name }}
                  </v-chip>
                </v-chip-group>
                <v-chip-group v-else>
                  <v-chip
                    small
                    outlined
                    color="secondary"
                    disabled
                    class="ma-1"
                  >
                    利用可能な集計がありません
                  </v-chip>
                </v-chip-group>
              </v-card>
            </div>
          </template>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :ripple="false"
          @click="$emit('close')"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :ripple="false"
          :disabled="!selectedTemplate"
          :loading="submitting"
          @click="submit"
        >
          登録
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { mdiClose } from '@mdi/js'
import UserAggregationAssignTemplateApi from '@/api/v2/eod-closing/UserAggregationAssignTemplate'
import { handleApiError } from '@/utils/errorHandler'

export default {
  name: 'AssignAggregationTemplateModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: [Number, String],
      default: null,
    },
    userName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const loading = ref(false)
    const submitting = ref(false)
    const templates = ref([])
    const selectedTemplate = ref(null)

    const fetchTemplates = async () => {
      loading.value = true
      try {
        const res = await UserAggregationAssignTemplateApi.getTemplates()
        templates.value = res.data.data.data || []
      } catch (e) {
        handleApiError(e, { $toast: vm.$toast })
        templates.value = []
      } finally {
        loading.value = false
      }
    }

    // 開くたびに選択をリセットして一覧を取り直す
    watch(() => props.isOpen, opened => {
      if (!opened) return
      selectedTemplate.value = null
      fetchTemplates()
    })

    const submit = async () => {
      if (!selectedTemplate.value || props.userId == null) return
      submitting.value = true
      try {
        await UserAggregationAssignTemplateApi.assignUser(selectedTemplate.value.id, props.userId)
        vm.$toast.success('集計表に登録しました。集計を再実行すると反映されます。')
        emit('assigned')
        emit('close')
      } catch (e) {
        handleApiError(e, { $toast: vm.$toast })
      } finally {
        submitting.value = false
      }
    }

    return {
      loading,
      submitting,
      templates,
      selectedTemplate,
      submit,
      icons: {
        mdiClose,
      },
    }
  },
}
</script>
